import { CommonModule } from '@angular/common';
import { Component, ElementRef, afterEveryRender, inject, input, viewChild, computed, signal } from '@angular/core';
import { GcmMatDialogService } from '@gcm/gcm-ui-angular';
import { TourMatMenu } from 'ngx-ui-tour-md-menu';
import { BaseAssetTypeDetails, InfrastructureDetails, PortfolioCompanyDetails, RealEstateDetails } from 'src/app/models/classes/asset-dashboard.class';
import { Asset } from 'src/app/models/classes/asset.class';
import { HoldingAsset } from 'src/app/models/classes/holding-asset.class';
import { DateFormatPipe } from 'src/app/pipes/date-format.pipe';
import { SafeHtmlPipe } from 'src/app/pipes/safe-html.pipe';
import { StandardStringFormatPipe } from 'src/app/pipes/standard-string-format.pipe';
import { ActivityTrackingService } from 'src/app/services/activity-tracking.service';
import { ImageCarouselComponent } from 'src/app/shared/image-carousel/image-carousel.component';

// Asset field interfaces
interface AssetField {
  label: string;
  value: string | number | Date | null;
  type: 'text' | 'date' | 'asIs';
  dataCy: string;
}

@Component({
    selector: 'app-asset-summary',
    imports: [
        CommonModule,
        ImageCarouselComponent,
        StandardStringFormatPipe,
        DateFormatPipe,
        SafeHtmlPipe,
        TourMatMenu
    ],
    templateUrl: './asset-summary.component.html',
    styleUrls: ['./asset-summary.component.scss']
})
export class AssetSummaryComponent {
  private dialogService = inject(GcmMatDialogService);
  private activityTracking = inject(ActivityTrackingService);

  readonly assetType = input<string | undefined>(undefined);
  readonly assetData = input<BaseAssetTypeDetails | Asset | HoldingAsset>();
  readonly title = input<string | undefined>(undefined);
  readonly summary = input<string | undefined>(undefined);
  readonly logo = input<string | undefined>(undefined);
  readonly slidingImgs = input<{ src: string }[]>([]);
  readonly showingOnDealDashboard = input<boolean>(false);
  readonly showingOnPositionDashboard = computed(() => this.showingOnDealDashboard());
  readonly showSummary = computed(() => {
    const summary = this.summary();
    return summary && summary !== '';
  });
  readonly showLogo = computed(() => {
    const logo = this.logo();
    return logo && logo !== '';
  });
  readonly showGallery = computed(() => {
    const slidingImgs = this.slidingImgs();
    return slidingImgs && slidingImgs.length !== 0;
  });
  showMoreButton = signal(false);
  readonly summaryEl = viewChild<ElementRef>('summaryElement');

  // Computed property for asset fields based on asset type
  readonly assetFields = computed<AssetField[]>(() => {
    const assetType = this.assetType();
    const assetData = this.assetData();
    
    if (!assetType || !assetData) {
      return [];
    }

    switch (assetType) {
      case 'Infrastructure':
        return this.getInfrastructureFields(assetData as InfrastructureDetails);
      
      case 'Real Estate':
        return this.getRealEstateFields(assetData as RealEstateDetails);
      
      case 'Portfolio Company':
        return this.getPortfolioCompanyFields(assetData as PortfolioCompanyDetails);
      
      default:
        return [];
    }
  });

  private getInfrastructureFields(asset: InfrastructureDetails): AssetField[] {
    return ([
      { label: 'Sector', value: asset.sector, type: 'text', dataCy: 'assetSector' },
      { label: 'Security', value: asset.securityType, type: 'text', dataCy: 'standardFormatValue' },
      { label: 'Industry', value: asset.industry, type: 'text', dataCy: 'assetIndustry' },
      { label: 'Region', value: asset.region, type: 'text', dataCy: 'assetRegion' },
      { label: 'Country', value: asset.country, type: 'text', dataCy: 'assetCountry' },
      { label: 'Investment Date', value: asset.initialInvestmentDate, type: 'date', dataCy: 'dateValue' },
      { label: 'Risk Profile', value: asset.riskProfile, type: 'text', dataCy: 'standardFormatValue' },
      { label: 'Stage', value: asset.stage, type: 'text', dataCy: 'standardFormatValue' }
    ] as AssetField[]);
  }

  private getRealEstateFields(asset: RealEstateDetails): AssetField[] {
    return ([
      { label: 'Sector', value: asset.sector, type: 'text', dataCy: 'assetSector' },
      { label: 'Geography', value: asset.geography, type: 'text', dataCy: 'standardFormatValue' },
      { label: 'Investment Date', value: asset.initialInvestmentDate, type: 'date', dataCy: 'dateValue' },
      { label: 'Square Feet', value: asset.squareFeet, type: 'asIs', dataCy: 'emptyValue' },
      { label: 'Number of Buildings', value: asset.numberOfBuildings, type: 'asIs', dataCy: 'emptyValue' },
      { label: 'Acres', value: asset.acres, type: 'asIs', dataCy: 'emptyValue' }
    ] as AssetField[]);
  }

  private getPortfolioCompanyFields(asset: PortfolioCompanyDetails): AssetField[] {
    return ([
      { label: 'Industry', value: asset.industry, type: 'text', dataCy: 'assetIndustry' },
      { label: 'Sector', value: asset.sector, type: 'text', dataCy: 'assetSector' },
      { label: 'Region', value: asset.region, type: 'text', dataCy: 'assetRegion' },
      { label: 'Country', value: asset.country, type: 'text', dataCy: 'assetCountry' },
      { label: 'Fiscal Year End', value: asset.fiscalYearEnd, type: 'text', dataCy: 'standardFormatValue' },
      { label: 'Investment Date', value: asset.initialInvestmentDate, type: 'date', dataCy: 'dateValue' },
      { label: 'Number of Employees', value: asset.numberOfEmployees, type: 'asIs', dataCy: 'emptyValue' },
      { label: 'Number of Locations', value: asset.numberOfLocations, type: 'asIs', dataCy: 'emptyValue' }
    ] as AssetField[]);
  }

  containerDataCy = computed(() => {
    const assetType = this.assetType();
    switch (assetType) {
      case 'Infrastructure':
        return 'infrastructureContainer';
      case 'Real Estate':
        return 'realEstateContainer';
      case 'Portfolio Company':
        return 'portfolioCompanyContainer';
      default:
        return '';
    }
  });



  constructor() {
    afterEveryRender(() => {
      const el = this.summaryEl();
      if (el) {
        this.showMoreButton.set(
          el.nativeElement.offsetHeight < el.nativeElement.scrollHeight ||
          el.nativeElement.offsetWidth < el.nativeElement.scrollWidth
        );
      }
    });
  }

  openModal() {
    const title = this.title();
    const assetId = (this.assetData() as Asset)?.masterId ?? (this.assetData() as BaseAssetTypeDetails)?.assetId;
    this.activityTracking.viewMoreSummary(assetId ?? '', title ?? '');
    this.dialogService.showInformationDialog(title ?? '', this.summary() ?? '', 'OK', 'information-dialog');
  }
}
